import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { experienceApi, type AchievementItem, type AttendanceProgress } from "@/lib/api/earnings";
import { ApiRequestError } from "@/lib/api/client";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";
import { cn } from "@/lib/utils/cn";

const KIND_ICON: Record<string, string> = {
  punctual_week: "clock",
  punctual_month: "clock",
  consistent_week: "calendar-check",
  consistent_month: "calendar-check",
  goal_completed: "target",
  tasks_on_time_week: "check-square",
  first_day: "check-circle",
  milestone_25: "trending-up",
  milestone_50: "trending-up",
  milestone_75: "trending-up",
  milestone_100: "gift",
};

/** Attendance progress, achievements and the rules behind them. A celebration
 * is shown once, only for something genuinely newly earned, and is then marked
 * as seen so it never plays again. Achievements are recognition only: they do
 * not change pay. */
export function AchievementsCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: qk.earnings.achievements, queryFn: experienceApi.achievements, gcTime: 5 * 60 * 1000 });
  const [showRules, setShowRules] = useState(false);
  const [celebrating, setCelebrating] = useState<AchievementItem[]>([]);

  const seen = useMutation({
    mutationFn: (ids: string[]) => experienceApi.markSeen(ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.earnings.achievements }),
  });

  const fresh = useMemo(() => (data?.items ?? []).filter((i) => i.new), [data]);
  useEffect(() => {
    if (fresh.length === 0 || celebrating.length > 0) return;
    // Show the celebration, then record it was shown so a refresh does not repeat it.
    setCelebrating(fresh);
    seen.mutate(fresh.map((i) => i.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh]);

  if (isLoading) {
    return (
      <Card aria-busy="true" data-testid="achievements-loading">
        <CardBody className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardBody>
      </Card>
    );
  }
  if (error) {
    if (error instanceof ApiRequestError && (error.status === 403 || error.status === 404)) return null;
    return (
      <Card>
        <ErrorState title="Couldn't load your progress" onRetry={() => refetch()} className="py-8" />
      </Card>
    );
  }
  if (!data) return null;

  const shown = data.items.slice(0, 8);

  return (
    <Card data-testid="achievements-card" className="dark:border-slate-700 dark:bg-slate-900">
      <CardHeader>
        <CardTitle className="dark:text-slate-100">Progress &amp; achievements</CardTitle>
      </CardHeader>
      <CardBody className="space-y-5">
        {celebrating.length > 0 && <Celebration items={celebrating} />}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ProgressBar progress={data.week} testId="progress-week" />
          <ProgressBar progress={data.month} testId="progress-month" />
        </div>

        {data.enabled && shown.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="achievements-empty">
            No achievements yet. They appear as you complete weeks and months of attendance.
          </p>
        )}
        {!data.enabled && (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="achievements-off">
            Achievements are switched off for your company.
          </p>
        )}
        {shown.length > 0 && (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="achievements-list">
            {shown.map((a) => (
              <li key={a.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                  <Icon name={KIND_ICON[a.achievement] ?? "check-circle"} className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{a.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{a.description}</p>
                  {a.period_label && <p className="text-xs text-slate-400 dark:text-slate-500">{a.period_label}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div>
          <button
            type="button"
            onClick={() => setShowRules((v) => !v)}
            aria-expanded={showRules}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline dark:text-primary-300"
          >
            <Icon name={showRules ? "chevron-down" : "chevron-right"} className="h-4 w-4" />
            How this is scored
          </button>
          {showRules && (
            <dl className="mt-2 space-y-2" data-testid="achievement-rules">
              {data.rules.map((r) => (
                <div key={r.name}>
                  <dt className="text-sm font-medium text-slate-700 dark:text-slate-200">{r.name}</dt>
                  <dd className="text-xs text-slate-500 dark:text-slate-400">{r.rule}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function ProgressBar({ progress, testId }: { progress: AttendanceProgress; testId: string }) {
  if (!progress || !progress.label) return null;
  const pct = Math.max(0, Math.min(100, progress.percent));
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>{progress.label}</span>
        <span className="tabular-nums">
          {progress.attended} of {progress.working_days} days
          {progress.late > 0 ? ` · ${progress.late} late` : ""}
        </span>
      </div>
      <div
        className="mt-1.5 h-2 rounded-full bg-slate-100 dark:bg-slate-800"
        role="progressbar"
        aria-label={`${progress.label} attendance`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div className="h-full rounded-full bg-primary-600 transition-[width] duration-700 ease-out motion-reduce:transition-none" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** A quiet one-time banner: it eases in and stays until the page is left. */
function Celebration({ items }: { items: AchievementItem[] }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div
      role="status"
      data-testid="achievement-celebration"
      className={cn(
        "rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 transition duration-500 ease-out motion-reduce:transition-none dark:border-emerald-800 dark:bg-emerald-950/50",
        shown ? "translate-y-0 scale-100 opacity-100" : "translate-y-1 scale-[0.98] opacity-0 motion-reduce:translate-y-0 motion-reduce:scale-100 motion-reduce:opacity-100",
      )}
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-200">
        <Icon name="gift" className="h-4 w-4" />
        {items.length === 1 ? "You earned a new achievement" : `You earned ${items.length} new achievements`}
      </p>
      <ul className="mt-1 text-sm text-emerald-900/90 dark:text-emerald-200">
        {items.slice(0, 4).map((i) => (
          <li key={i.id}>{i.title}</li>
        ))}
        {items.length > 4 && <li>and {items.length - 4} more</li>}
      </ul>
    </div>
  );
}
